import { create_analyse_with_upload,
    deleteAnalyse,
    fetchAnalysesSummary,
    guessAnalyseMetadata,
    updateAnalyseMetadata
} from "../Services/review.service";
import { IAStreamActionTypes, ReviewActionTypes, StageActionTypes } from "./actionTypes";
import { message } from "antd";
import i18n from "../i18n";
import {setGlobalLoading} from "./documents.action";
import { setCinematicTarget } from "../Reducers/uiReducer";
import { getIAReviewResults } from "../Services/stage.service";
import {get_criteria_for_analysis} from "../Services/critere.service";
import {hydrateLatestReferenceReviewSnapshot} from "./referenceReview.action";
import {getAnalysisPov, hasCompletePov, isSamePov} from "../utils/pov";

function clearCriteriaExecutionProjection(dispatch, getState) {
    const criteria = getState?.().stage?.criteriaListe;
    if (!Array.isArray(criteria)) return;
    dispatch({
        type: StageActionTypes.SET_CRITERIA_STAGE,
        payload: criteria.map((criterion) => ({
            ...criterion,
            status: "not_executed",
            executed_at: null,
        })),
    });
}

function pickAnalyseId(obj) {
    if (!obj || typeof obj !== 'object') return null;
    
    // Cherche dans obj directement
    if (obj.analyse_id) return obj.analyse_id;
    if (obj.analysis_id) return obj.analysis_id;
    if (obj.analysisId) return obj.analysisId;
    if (obj.id) return obj.id;
    
    // Cherche dans obj.data (si le backend wrap dans { data: {...} })
    if (obj.data && typeof obj.data === 'object') {
        if (obj.data.analyse_id) return obj.data.analyse_id;
        if (obj.data.analysis_id) return obj.data.analysis_id;
        if (obj.data.analysisId) return obj.data.analysisId;
        if (obj.data.id) return obj.data.id;
    }
    
    // Cherche dans obj.analyse ou obj.analysis
    if (obj.analyse?.id) return obj.analyse.id;
    if (obj.analysis?.id) return obj.analysis.id;
    
    return null;
}

export function RequestCreateAnalyseWithUpload(file, metadataDraft) {
    return async (dispatch) => {
        try {
            dispatch(setGlobalLoading(true));

            const data = await create_analyse_with_upload(file, metadataDraft);

            const analyseId = pickAnalyseId(data);
            
            if (!analyseId) {
                console.error("Réponse create_analyse_with_upload inattendue:", data);
                throw new Error("analyse_id manquant - vérifier la console pour voir la réponse API");
            }

            // Stocker l'ID destination dans Redux - la navigation sera faite par l'overlay après l'animation
            dispatch(setCinematicTarget({ analyseId, metadataDraft }));
        } catch (error) {
            dispatch(setReviewError(
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.response?.data?.detail ||
                error.message ||
                i18n.t("review:messages.server")
            ));
            throw error;
        } finally {
            dispatch(setGlobalLoading(false));
        }
    };
}

export function RequestUpdateAnalyseMetadata(id, payload) {
    return async (dispatch, getState) => {
        const stateBeforeUpdate = getState?.() || {};
        const previousPov = getAnalysisPov(stateBeforeUpdate.stage?.main_document);
        const expectedPov = getAnalysisPov(payload);
        const hasPartyRole = Object.prototype.hasOwnProperty.call(payload, "party_role");
        const hasStudiedPartyName = Object.prototype.hasOwnProperty.call(payload, "studied_party_name");
        const povChanged = (hasPartyRole || hasStudiedPartyName)
            && !isSamePov(previousPov, expectedPov);
        const currentVersionId = stateBeforeUpdate.documentVersion?.currentVersion
            || stateBeforeUpdate.documentVersion?.content?.id
            || stateBeforeUpdate.stage?.versions?.[0]?.id;
        let povPersisted = false;

        if (povChanged) {
            // This must happen before the PUT starts so POV A is never displayed
            // while POV B is being persisted. A remains in Redux for rollback.
            dispatch({type: IAStreamActionTypes.SET_ID_LOADING_COMMENTS, payload: true});
            dispatch({type: IAStreamActionTypes.RESET_FAST_GLOBAL_STREAM});
            dispatch({type: IAStreamActionTypes.RESET_ANALYSIS_REASONING});
        }

        try {
            const res = await updateAnalyseMetadata(id, payload);
            povPersisted = povChanged;
            
            // Mettre à jour le store Redux avec les données sauvegardées
            // Cela permet à la validation de isMetadataIncompleteForAnalysis de fonctionner correctement
            const mainDocumentPatch = {};
            [
                "document_type",
                "party_role",
                "studied_party_name",
                "industry",
                "jurisdiction",
            ].forEach((field) => {
                if (Object.prototype.hasOwnProperty.call(payload, field)) {
                    mainDocumentPatch[field] = payload[field];
                }
            });
            dispatch({
                type: StageActionTypes.SET_STAGE_MAIN_DOCUMENT,
                payload: mainDocumentPatch,
            });
            
            // Mettre à jour le context si fourni
            if (payload.context !== undefined) {
                dispatch({
                    type: StageActionTypes.SET_STAGE_CONTEXT,
                    payload: payload.context
                });
            }

            if (povChanged) {
                // The PUT committed POV B. Only now invalidate every POV A
                // projection, including the persisted Reference Review snapshot.
                dispatch({type: IAStreamActionTypes.FLUSH_IA_COMMENTS});
                clearCriteriaExecutionProjection(dispatch, getState);
                try {
                    if (currentVersionId) {
                        const commentsPromise = getIAReviewResults(id, currentVersionId);
                        const criteriaPromise = get_criteria_for_analysis(id, currentVersionId);
                        const referencePromise = hasCompletePov(expectedPov)
                            ? dispatch(hydrateLatestReferenceReviewSnapshot(
                                id,
                                currentVersionId,
                                expectedPov,
                            ))
                            : Promise.resolve(null);
                        const [iaCommentsRes, criteria] = await Promise.all([
                            commentsPromise,
                            criteriaPromise,
                            referencePromise,
                        ]);
                        if (isSamePov(getState?.().stage?.main_document, expectedPov)) {
                            dispatch({
                                type: IAStreamActionTypes.SET_IA_COMMENTS,
                                payload: iaCommentsRes.data
                            });
                            dispatch({
                                type: StageActionTypes.SET_CRITERIA_STAGE,
                                payload: criteria.data,
                            });
                        }
                    }
                } catch (refreshError) {
                    console.error("Impossible de recharger les commentaires du POV sélectionné", refreshError);
                    message.warning(i18n.t("review:messages.povCommentsReloadError"));
                    const committedError = new Error("pov_results_reload_failed", {
                        cause: refreshError,
                    });
                    committedError.povPersisted = true;
                    committedError.analysisPov = expectedPov;
                    throw committedError;
                }
            }
            
            dispatch(RequestFetchAnalysesSummary());
            return {
                response: res,
                povChanged,
                analysisPov: expectedPov,
            };
        } catch (error) {
            if (!povPersisted) {
                dispatch(setReviewError(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    error.response?.data?.detail ||
                    i18n.t("review:messages.server")
                ));
            }
            throw error;
        } finally {
            if (povChanged) {
                dispatch({type: IAStreamActionTypes.SET_ID_LOADING_COMMENTS, payload: false});
            }
        }
    };
}

export function RequestReloadActivePovResults(id, documentVersionId) {
    return async (dispatch, getState) => {
        const expectedPov = getAnalysisPov(getState?.().stage?.main_document);
        dispatch({type: IAStreamActionTypes.SET_ID_LOADING_COMMENTS, payload: true});
        dispatch({type: IAStreamActionTypes.FLUSH_IA_COMMENTS});
        clearCriteriaExecutionProjection(dispatch, getState);
        try {
            const commentsPromise = getIAReviewResults(id, documentVersionId);
            const criteriaPromise = get_criteria_for_analysis(id, documentVersionId);
            const referencePromise = hasCompletePov(expectedPov)
                ? dispatch(hydrateLatestReferenceReviewSnapshot(
                    id,
                    documentVersionId,
                    expectedPov,
                ))
                : Promise.resolve(null);
            const [iaCommentsRes, criteria] = await Promise.all([
                commentsPromise,
                criteriaPromise,
                referencePromise,
            ]);
            if (!isSamePov(getState?.().stage?.main_document, expectedPov)) {
                return {stale: true};
            }
            dispatch({
                type: IAStreamActionTypes.SET_IA_COMMENTS,
                payload: iaCommentsRes.data,
            });
            dispatch({
                type: StageActionTypes.SET_CRITERIA_STAGE,
                payload: criteria.data,
            });
            return {stale: false, analysisPov: expectedPov};
        } finally {
            dispatch({type: IAStreamActionTypes.SET_ID_LOADING_COMMENTS, payload: false});
        }
    };
}

export function RequestGuessAnalyseMetadata(id, payload) {
    return async dispatch => {
        try {
            const res = await guessAnalyseMetadata(id, payload);
            return res?.data ?? res;
        } catch (error) {
            dispatch(setReviewError(
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.response?.data?.detail ||
                i18n.t("review:messages.server")
            ));
            throw error;
        }
    };
}

export function RequestFetchAnalysesSummary() {
    return async dispatch => {
        try {
            dispatch({ type: ReviewActionTypes.SET_LOADING, payload: true });
            const result = await fetchAnalysesSummary();
            dispatch({ type: ReviewActionTypes.SET_ANALYSES_SUMMARY, payload: result.data });
        } catch (error) {
            dispatch(setReviewError(
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.response?.data?.detail ||
                i18n.t("review:messages.fetchError")
            ));
        } finally {
            dispatch({ type: ReviewActionTypes.SET_LOADING, payload: false });
        }
    };
}

export function RequestDeleteAnalyse(id, { notify = true } = {}) {
    return async dispatch => {
        try {
            await deleteAnalyse(id);
            dispatch({ type: ReviewActionTypes.REMOVE_ANALYSE_SUMMARY, payload: id });
            if (notify) {
                message.success(i18n.t("review:messages.deleted"));
            }
            return { success: true, id };
        } catch (error) {
            dispatch(setReviewError(
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.response?.data?.detail ||
                i18n.t("review:messages.server")
            ));
            return { success: false, id, error };
        }
    };
}


export const setReviewError = (errorMessage) => ({
    type: ReviewActionTypes.SET_ERROR,
    payload: errorMessage,
});
